import {
    DataTableCustomized,
    type DataTableAction,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useModal } from '@/contexts/modal'
import { useConfirmation } from '@/contexts/confirmation'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { CreateEditPricingStepModalProps } from '@/modals/PricingStepModals/CreateEdit'
import { deletePricingStep } from '@/modules/Settings/services/product-pricing-step'
import {
    ProductConfigOption,
    ProductPricingStep,
} from '@/modules/Settings/types/product'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'vnetwork-ui'
import { getPricingStepColumns } from './product-columns'

/** Pricing steps sub-table with CRUD for a single config option */
const ExpandedPricingSteps: React.FC<{
    productId: number
    option: ProductConfigOption
    readOnly?: boolean
}> = ({ productId, option, readOnly }) => {
    const { t } = useTranslation('settings')
    const { setModal } = useModal()
    const { setConfirmation } = useConfirmation()

    const pricingStepColumns = useMemo(() => getPricingStepColumns(t), [t])

    const { mutateAsync: doDelete } = useMutationWithHandler(
        {
            mutationFn: (stepId: number) =>
                deletePricingStep(
                    productId,
                    option.product_config_option_id,
                    stepId
                ),
        },
        {
            successMessage: t('pricing-step-deleted-successfully'),
            reactQueryKeys: [QUERY_KEYS.PRODUCT_CONFIG_OPTIONS],
        }
    )

    const handleCreate = useCallback(() => {
        setModal<CreateEditPricingStepModalProps>(
            'PricingStepModals/CreateEdit/index',
            {
                productId,
                optionId: option.product_config_option_id,
                pricingStep: null,
            }
        )
    }, [setModal, productId, option.product_config_option_id])

    const handleEdit = useCallback(
        (step: ProductPricingStep) => {
            setModal<CreateEditPricingStepModalProps>(
                'PricingStepModals/CreateEdit/index',
                {
                    productId,
                    optionId: option.product_config_option_id,
                    pricingStep: step,
                }
            )
        },
        [setModal, productId, option.product_config_option_id]
    )

    const handleDelete = useCallback(
        (step: ProductPricingStep) => {
            setConfirmation({
                open: true,
                title: t('delete-pricing-step'),
                description: t('confirm-delete-pricing-step'),
                onConfirm: () => doDelete(step.product_pricing_step_id),
            })
        },
        [setConfirmation, doDelete, t]
    )

    const pricingStepActions: DataTableAction<ProductPricingStep>[] = useMemo(
        () => [
            {
                key: 'edit',
                label: t('edit'),
                icon: <Pencil className="w-4 h-4" />,
                variant: 'info',
                onClick: (row) => handleEdit(row),
            },
            {
                key: 'delete',
                label: t('delete'),
                icon: <Trash2 className="w-4 h-4" />,
                variant: 'destructive',
                onClick: (row) => handleDelete(row),
            },
        ],
        [t, handleEdit, handleDelete]
    )

    return (
        <div className="py-2">
            <div className="flex items-center justify-between px-2 pb-1">
                <span className="text-xs font-semibold text-muted-foreground">
                    {t('pricing-steps')} (
                    {option.product_pricing_steps?.length || 0})
                </span>
                {!readOnly && (
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleCreate}
                    >
                        <Plus className="w-3 h-3" />
                        {t('add-pricing-step')}
                    </Button>
                )}
            </div>
            <DataTableCustomized<ProductPricingStep>
                columns={pricingStepColumns}
                rows={option.product_pricing_steps || []}
                rowKey={(r) => r.product_pricing_step_id}
                emptyMessage={t('no-pricing-steps')}
                actions={readOnly ? undefined : pricingStepActions}
                bordered
            />
        </div>
    )
}

export default ExpandedPricingSteps
